import { generateInitialRank, generateNextRank } from './lexorank'
import { logger } from '@/lib/monitoring/logger'

interface SortableItem {
  id: string
  sortOrder?: string | null | undefined
}

/**
 * Rebalances sort orders for a list of items to maintain proper lexicographic ordering.
 * When items are reordered frequently, sort order values can become clustered.
 * This redistributes them evenly.
 */
export async function rebalanceSortOrders<T extends SortableItem>(
  items: Array<T>,
  updateFn: (id: string, updates: { sortOrder: string }) => Promise<void>,
): Promise<void> {
  if (items.length === 0) {
    return
  }

  const updates: Array<{ id: string; sortOrder: string }> = []
  let previousRank: string | null = null

  items.forEach((item, index) => {
    const nextRank =
      index === 0 ? generateInitialRank() : generateNextRank(previousRank!)

    previousRank = nextRank

    if (item.sortOrder !== nextRank) {
      updates.push({ id: item.id, sortOrder: nextRank })
    }
  })

  if (updates.length === 0) {
    return
  }

  for (const { id, sortOrder } of updates) {
    try {
      await updateFn(id, { sortOrder })
    } catch (error) {
      logger.logError(error as Error, 'DATABASE', {
        itemId: id,
        operation: 'rebalance_sort_order',
      })
    }
  }
}
